/* eslint-disable react/no-danger */
import { Loading3QuartersOutlined } from "@ant-design/icons";
import SharedComponents, { Alert, Avatar, MessageSummary } from "@audacia-hq/shared/components";
import { FileUpload } from "@audacia-hq/shared/design-system";
import { daysUntil, generateFileName, secondsToHms } from "@audacia-hq/shared/utils";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Spin } from "antd";
import dayjs from "dayjs";
import { FormikProvider, useFormik } from "formik";
import Link from "next/link";
import * as React from "react";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";

import { useLocale } from "../../context/LocaleContext";
import { useMember } from "../../context/MemberContext";
import { GET_EXPERT_LIMITS, GET_MESSAGES_SUMMARY, IS_MESSAGE_ALLOWED } from "../../graphql/query";
import { useFetcher } from "../../hooks/useClientFetcher";
import { Expert } from "../../models/expert";
import { createMessage } from "../../services/api";
import { uploadFile } from "../../services/apiClients/storageAPI";
import Button from "../Button";
import VisitorSignup from "../consultations/VisitorSignup";

interface Props {
  onClose: () => void;
  expert: Expert;
}

const PrivateMessageModal: React.FC<Props> = ({
  onClose, expert,
}) => {
  const { t } = useTranslation("member");
  const locale = useLocale();
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState(false);
  const { member } = useMember();
  const [error, setError] = useState<string>();

  const [_isMessageAllowed, { data: messageAllowed, loading: loadingAllowed }] = useFetcher<{
    messageAllowed: boolean
  }>(IS_MESSAGE_ALLOWED, { variables: { correspondent: expert?.expertUid } });
  const [_getMessagesSummary, { data: messagesSummary, loading: loadingSummary }] = useFetcher<{
    messagesSummary: MessageSummary[]
  }>(GET_MESSAGES_SUMMARY, { variables: { correspondent: expert?.expertUid } });

  const [_getExpertLimits, { data: expertLimits }] = useFetcher<{
    expertLimits: { consultationSecondCtr: number, availableMessageNbr: number }
  }>(GET_EXPERT_LIMITS, { variables: { expertUid: expert?.expertUid } });

  const maxFiles = 2;
  const maxFileSize = 4 * 1024 ** 2; // 4MB
  const mimeTypes = {
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
    "image/bmp": [".bmp"],
  };
  const writtenMimeTypes: (keyof typeof mimeTypes)[] = ["image/jpeg", "image/png"];

  const formik = useFormik({
    initialValues: {
      message: "", attachments: [],
    },
    validationSchema: Yup.object().shape({
      message: Yup.string().required("required"),
      attachments: Yup.array().of(Yup.mixed().test("fileSize", t("common:errors.files.size", {
        maxSize: new Intl.NumberFormat(locale, {
          unit: "megabyte",
          unitDisplay: "narrow",
          style: "unit",
        }).format(maxFileSize / 1024 ** 2),
      }), (value) => value && (value as any).size <= maxFileSize)),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (form) => {
      if (!expert) return;

      try {
        setLoading(true);
        setError(undefined);

        const urls = await Promise.all(
          form.attachments.map((file) => uploadFile(file, generateFileName(file), "messages", [
            member.memberUid,
            expert.expertUid,
          ])),
        );

        const { error } = await createMessage({
          variables: {
            recipient: expert.expertUid,
            content: form.message,
            language: locale,
            attachments: urls,
          },
        });

        if (error) {
          throw new Error(error.messages[0]);
        }
        setConfirmed(true);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    },
  });

  const renderConfirmed = () => (
    <>
      <div className="col-span-6 md:col-span-4 md:col-start-2" data-pw="privateMessageConfirmed">
        <div>
          <div className="w-full text-center ">

            <FontAwesomeIcon
              className="text-6xl z-10 text-green-600 mb-7"
              icon={faCircleCheck}
            />
            <div className="text-lg font-semibold ">{t("privateMessage.confirmationTitle")}</div>
            <div className="mt-3">
              {t("privateMessage.confirmationSent", {
                pseudo: expert?.pseudo,
              })}
            </div>
          </div>
        </div>
        <div className="mt-10 text-end">
          <Button
            size="medium"
            render="primary"
            onClick={() => onClose()}
            data-pw="modalClose"
          >
            <span>{t("common:close")}</span>
          </Button>
        </div>
      </div>
    </>
  );

  const renderBlockedBy = () => (
    <div data-pw="privateMessageBlocked">
      <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
        <div className="text-lg font-semibold py-2 text-center">{t("privateMessage.blockedByTitle")}</div>
        <div className="flex flex-col gap-4 text-center">

          <div>
            {t("privateMessage.blockedByExplaination", {
              pseudo: expert?.pseudo,
            })}
          </div>
        </div>
      </div>

      <div className="mt-10 text-end">
        <Button
          data-pw="modalClose"
          size="medium"
          render="primary"
          onClick={() => onClose()}
        >
          <span>{t("common:close")}</span>
        </Button>
      </div>
    </div>
  );

  const renderExpertDisabled = () => (
    <div data-pw="privateMessageExpertDisabled">
      <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
        <div className="text-lg font-semibold py-2 text-center">{t("privateMessage.expertDisabledTitle")}</div>
        <div className="flex flex-col gap-4 text-center">

          <div>
            {t("privateMessage.expertDisabledExplaination", {
              pseudo: expert?.pseudo,
            })}
          </div>
        </div>
      </div>

      <div className="mt-10 text-end">
        <Button
          data-pw="modalClose"
          size="medium"
          render="primary"
          onClick={() => onClose()}
        >
          <span>{t("common:close")}</span>
        </Button>
      </div>
    </div>
  );

  const renderUnconsulted = () => (
    <>
      <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
        <div className="text-lg font-semibold py-2 text-center">{t("privateMessage.unconsultedTitle")}</div>
        <div className="flex flex-col gap-4 text-center">

          <div>
            {t("privateMessage.unconsultedExplaination", {
              pseudo: expert?.pseudo,
            })}
          </div>
        </div>
      </div>

      <div className="mt-10 text-end">
        <Button
          data-pw="modalClose"
          size="medium"
          render="primary"
          onClick={() => onClose()}
        >
          <span>{t("common:close")}</span>
        </Button>
      </div>
    </>
  );

  const requiredTime = useMemo(() => {
    const requiredSeconds = 3600;
    const elapsedSeconds = expertLimits?.expertLimits?.consultationSecondCtr ?? 0;

    return requiredSeconds - elapsedSeconds;
  }, [expertLimits?.expertLimits?.consultationSecondCtr]);

  return (
    <SharedComponents.Modal
      mobileFullscreen
      show
      onClose={onClose}
      title={(
        <div className="flex gap-3 md:gap-6 font-normal">
          <Avatar
            name={expert.pseudo}
            uid={expert.expertUid}
            src={expert.picture}
            loading="lazy"
            height={100}
            width={100}
            className="rounded-lg shrink-0"
          />
          {member && expertLimits ? (

            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-x-3 items-center text-sm sm:text-base">
                <span className="font-semibold">
                  <span>{expert.pseudo}</span>
                </span>

                {parseInt(expert?.expertCode, 10) !== 0 && (
                  <SharedComponents.Tooltip
                    position="bottom"
                    className="w-48"
                    arrow
                    message={t("common:accessCodeTips")}
                  >
                    <span className="border border-slate-100 bg-slate-50 hover:bg-slate-100 text-gray-500 text-sm font-medium px-1 py-0.5 rounded">{String(expert.expertCode).padStart(3, "0")}</span>
                  </SharedComponents.Tooltip>
                )}

              </div>
              <p className="text-sm">
                <Trans
                  i18nKey="privateMessage.limits"
                  values={{
                    count: expertLimits?.expertLimits?.availableMessageNbr ?? 0,
                    time: secondsToHms(requiredTime).toTimeString(t, {
                      partial: true, truncate: true, padded: false, tokens: ["hour", "minute"], long: true,
                    }),
                    days: Math.max(1, daysUntil(
                      dayjs().utc().startOf("month").add(1, "month")
                        .add(1, "hour")
                        .toDate(),
                    )),
                  }}
                  t={t}
                  components={[<span className="font-semibold" />]}
                />
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="text-lg sm:text-xl font-semibold">
                {t("consultations:consultationForm.modalTitle.privateMessage")}
              </div>
              <div className="flex flex-wrap gap-x-3 items-end">
                <span className="">{expert.pseudo}</span>
                <SharedComponents.Tooltip
                  position="bottom"
                  className="w-48"
                  arrow
                  message={t("common:accessCodeTips")}
                >
                  <span className="border border-slate-100 bg-slate-50 hover:bg-slate-100 hover:cursor-default text-gray-800 text-sm font-medium px-1 py-0.5 rounded">{String(expert.expertCode).padStart(3, "0")}</span>
                </SharedComponents.Tooltip>
              </div>
            </div>
          )}
        </div>
      )}
    >
      {member && (
        <>
          {(loadingSummary || loadingAllowed) ? (
            <div className="text-center">
              <Spin indicator={<Loading3QuartersOutlined style={{ fontSize: 48, color: "black" }} spin />} />
              <p className="pt-4 text-sm text-gray-500">{t("common:loadingInProgress")}</p>
            </div>
          ) : (
            <>
              {parseInt(expert?.expertCode, 10) === 0 ? (
                renderExpertDisabled()
              ) : (
                <>
                  {!messageAllowed?.messageAllowed && renderUnconsulted()}
                  {messageAllowed?.messageAllowed && messagesSummary?.messagesSummary[0]?.blockedBy && renderBlockedBy()}
                  {messageAllowed?.messageAllowed && !messagesSummary?.messagesSummary[0]?.blockedBy && confirmed && renderConfirmed()}
                  {messageAllowed?.messageAllowed && !messagesSummary?.messagesSummary[0]?.blockedBy && !confirmed && (
                    <>
                      <FormikProvider value={formik}>
                        <div className="flex flex-col gap-1 my-3">
                          {messagesSummary?.messagesSummary[0]?.blocked && (
                            <div className="mb-5">
                              <Alert type="alert">
                                <p>
                                  {t("privateMessage.blocked", {
                                    pseudo: expert?.pseudo,
                                  })}
                                  <Link onClick={() => onClose()} href={`/messages?correspondentUid=${expert.expertUid}`} className="text-center underline">
                                    {t("privateMessage.unblock")}
                                  </Link>
                                </p>
                              </Alert>
                            </div>
                          )}
                          {error && (
                            <div className="mb-5">
                              <Alert type="alert"><p>{t(`errors.${error}`)}</p></Alert>
                            </div>
                          )}
                          <label htmlFor="message">
                            {t("privateMessage.message")}
                          </label>
                          <textarea
                            maxLength={1400}
                            className={`input ${formik.errors.message ? "border-red-500 border" : ""}`}
                            id="message"
                            rows={5}
                            value={formik.values.message}
                            onChange={formik.handleChange}
                          />
                          <p className="text-end text-sm">
                            {
                              formik.values.message?.length || 0
                            }
                            {" "}
                            /
                            {" "}
                            {
                              1400
                            }
                          </p>
                          <div className="flex justify-between items-center w-full">
                            <div className="text-red-500">
                              {formik.errors.message && t(`common:errors.${formik.errors.message}`)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <FileUpload
                            // Force IV font for parent and all children
                            className="!font-sans [&_*]:!font-sans"
                            title={
                              t("privateMessage.files")
                            }
                            uploadTitle={(
                              <Trans
                                t={t}
                                i18nKey="common:dropzone.initialInteractive"
                                components={[<span className="text-primary-500" />]}
                              />
                            )}
                            uploadLabel={t("common:dropzone.limit", {
                              formats: new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(
                                writtenMimeTypes.map((type) => mimeTypes[type][0]),
                              ),
                              size: new Intl.NumberFormat(locale, {
                                unit: "megabyte",
                                unitDisplay: "narrow",
                                style: "unit",
                              }).format(maxFileSize / 1024 ** 2),
                            })}
                            value={formik.values.attachments}
                            errors={formik.errors.attachments as any[]}
                            onChange={async (files) => {
                              await formik.setFieldValue("attachments", files);
                              formik.validateField("attachments");
                            }}
                            maxFiles={maxFiles}
                            fullWidth
                            accept={Object.keys(mimeTypes).concat(Object.values(mimeTypes).flat())}
                            UploadCardProps={(file) => ({
                              view: false,
                              download: false,
                              errorUsesDelete: true,
                              icon: Object.keys(mimeTypes).includes(file.type) ? (
                                <img
                                  src={
                                    URL.createObjectURL(file)
                                  }
                                  alt="file"
                                />
                              ) : null,
                            })}
                          />
                        </div>

                        <div className="pt-6 text-end">
                          <Button
                            size="medium"
                            render="primary"
                            onClick={() => formik.submitForm()}
                            loading={loading}
                            data-pw="consultationSubmit"
                          >
                            <span>{t("common:send")}</span>
                          </Button>
                        </div>

                      </FormikProvider>
                    </>
                  )}
                </>
              )}

            </>
          )}

        </>
      )}

      {!member && (
        <VisitorSignup />
      )}
    </SharedComponents.Modal>
  );
};

export default PrivateMessageModal;
